import { useUser } from 'core/lite/hooks/useUser';
import PropTypes from 'prop-types';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { CHATBOT_STATUS, isStreamingStatus } from '../constants/status';
import { ModelNameEnum } from '../models/ModelNameEnum';
import { useAudioManager } from './useAudioManager';
import { useStorageSync } from './useStorageSync';

/**
 * Chatbot width constraints
 */
const CHATBOT_MIN_WIDTH = 400;
const CHATBOT_MAX_WIDTH_PERCENTAGE = 0.5;
const CHATBOT_DEFAULT_WIDTH = 400;

/**
 * Action types for chatbot reducer
 */
const CHATBOT_ACTIONS = {
  OPEN_CHATBOT: 'OPEN_CHATBOT',
  CLOSE_CHATBOT: 'CLOSE_CHATBOT',
  SET_CHAT_CONTEXT: 'SET_CHAT_CONTEXT',
  SET_CURRENT_CONVERSATION_ID: 'SET_CURRENT_CONVERSATION_ID',
  SET_CONVERSATIONS: 'SET_CONVERSATIONS',
  SET_CONVERSATION_STATUS: 'SET_CONVERSATION_STATUS',
  SET_SYSTEM_PROMPT: 'SET_SYSTEM_PROMPT',
  SET_DEV_MODE: 'SET_DEV_MODE',
  SET_MODEL: 'SET_MODEL',
  SET_TEMPERATURE: 'SET_TEMPERATURE',
  SET_MAX_TOKENS: 'SET_MAX_TOKENS',
  SET_CHATBOT_MODE: 'SET_CHATBOT_MODE',
  SET_ACTIVITY_INSTANCE_ID: 'SET_ACTIVITY_INSTANCE_ID',
  SET_ACTIVITY_NAME: 'SET_ACTIVITY_NAME',
  SET_ACTIVITY_EXTRA_INFO: 'SET_ACTIVITY_EXTRA_INFO', // ← TODO: Remove after testing
  SET_ACTIVITY_ID: 'SET_ACTIVITY_ID',
  SET_PRACTICE_EXERCISE: 'SET_PRACTICE_EXERCISE',
  SET_PRACTICE_LOADING: 'SET_PRACTICE_LOADING',
  SET_PRACTICE_ERROR: 'SET_PRACTICE_ERROR',
  QUEUE_MESSAGE: 'QUEUE_MESSAGE',
  DEQUEUE_MESSAGE: 'DEQUEUE_MESSAGE',
  SET_COMPLETED_ACTIVITIES_COUNT: 'SET_COMPLETED_ACTIVITIES_COUNT',
  ADD_ACTIVITY_RESULT: 'ADD_ACTIVITY_RESULT',
  UPDATE_SESSION_KCS: 'UPDATE_SESSION_KCS',
  RESET_PRACTICE_SESSION: 'RESET_PRACTICE_SESSION',
  SET_CHATBOT_WIDTH: 'SET_CHATBOT_WIDTH',
  SET_ENABLE_EXTERNAL_SEARCH: 'SET_ENABLE_EXTERNAL_SEARCH',
  CLEAR_MESSAGE_QUEUE: 'CLEAR_MESSAGE_QUEUE',
  SET_IS_STREAMING_CANVAS: 'SET_IS_STREAMING_CANVAS',
  CLEAR_STREAMING_CANVAS: 'CLEAR_STREAMING_CANVAS',
  SET_CANVAS_CONTENT: 'SET_CANVAS_CONTENT',
  SET_CONVERSATION_DATA: 'SET_CONVERSATION_DATA',
  CLEAR_CONVERSATION_DATA: 'CLEAR_CONVERSATION_DATA',
  SET_CANVAS_TOOL_EXECUTING: 'SET_CANVAS_TOOL_EXECUTING',
};

/**
 * Load persisted UI state from localStorage
 */
const loadPersistedUIState = () => {
  try {
    const saved = localStorage.getItem('tangerine_chatbot_ui_state');
    return saved ? JSON.parse(saved) : {};
  } catch (error) {
    console.warn('Failed to load chatbot UI state:', error);
    return {};
  }
};

/**
 * Initial chatbot state
 */
/**
 * Detect chatbot mode based on URL pattern
 */
const detectChatbotMode = (pathname) => {
  // Map URL patterns to first-class modes
  if (pathname.startsWith('/activities/sessions/')) return 'practice';
  if (pathname.startsWith('/teacher-tools/')) return 'teacher-tools';
  if (pathname.startsWith('/results-assesment-individual/') || pathname.includes('/question/')) return 'revision';
  return 'study';
};

const getInitialChatbotState = () => {
  const persistedUI = loadPersistedUIState();
  // Always determine initial mode based on current URL (never persist mode)
  const currentPath = window.location.pathname;
  const initialMode = detectChatbotMode(currentPath);

  return {
    isChatbotOpen: persistedUI.isChatbotOpen || false,
    chatbotWidth: persistedUI.chatbotWidth || CHATBOT_DEFAULT_WIDTH,
    chatContext: { pageUrl: window.location.href },
    isDevMode:
      window.location.hostname === 'localhost' ||
      (window.location.hostname.includes('tenant-ai') && !window.location.hostname.includes('tenant-ai-2.oneclicklabs.es')),
    // Only load persisted conversationId if NOT in practice mode
    currentConversationId: initialMode === 'study' ? persistedUI.currentConversationId : null,
    conversations: [],
    conversationStatus: CHATBOT_STATUS.IDLE,
    isChatStreaming: false, // Derived streaming state
    systemPrompt: '',
    model: ModelNameEnum.GEMINI_2_5_PRO, // Default model
    temperature: 0.2, // Default temperature
    maxTokens: 2048, // Default max tokens
    mode: initialMode, // Always derived from URL, never persisted
    activityInstanceId: null, // For practice mode conversations
    activityName: '', // For practice mode activity name
    activityExtraInfo: null, // Extra info for activity display ← TODO: Remove after testing
    activityId: null, // Activity ID for practice mode
    practiceExercise: null, // Generated practice exercise data
    practiceIsLoading: true, // Practice exercise loading state
    practiceError: null, // Practice exercise error state
    messageQueue: [], // Queue for pending messages to be sent
    completedActivitiesCount: 0, // Track number of completed activities
    activityResults: [], // Store results of completed activities
    sessionKCs: [], // Knowledge components with mastery levels
    enableExternalSearch: false, // Enable external web search when RAG content is insufficient
    isStreamingCanvas: false,
    canvasContent: '',
    conversationData: null, // Full conversation object from server
    isCanvasToolExecuting: false, // True when canvas tool is executing (before first delta arrives)
  };
};

const initialChatbotState = getInitialChatbotState();

/**
 * Chatbot reducer function
 */
function chatbotReducer(state, action) {
  switch (action.type) {
    case CHATBOT_ACTIONS.OPEN_CHATBOT:
      return {
        ...state,
        isChatbotOpen: true,
      };
    case CHATBOT_ACTIONS.CLOSE_CHATBOT:
      return {
        ...state,
        isChatbotOpen: false,
      };
    case CHATBOT_ACTIONS.SET_CHAT_CONTEXT:
      return {
        ...state,
        chatContext: action.payload,
      };
    case CHATBOT_ACTIONS.SET_CURRENT_CONVERSATION_ID:
      return {
        ...state,
        currentConversationId: action.payload,
      };
    case CHATBOT_ACTIONS.SET_CONVERSATIONS:
      return {
        ...state,
        conversations: action.payload,
      };
    case CHATBOT_ACTIONS.SET_CONVERSATION_STATUS: {
      const newStatus = action.payload;
      return {
        ...state,
        conversationStatus: newStatus,
        isChatStreaming: isStreamingStatus(newStatus),
      };
    }
    case CHATBOT_ACTIONS.SET_SYSTEM_PROMPT:
      return {
        ...state,
        systemPrompt: action.payload,
      };
    case CHATBOT_ACTIONS.SET_DEV_MODE:
      return {
        ...state,
        isDevMode: action.payload,
      };
    case CHATBOT_ACTIONS.SET_MODEL:
      return {
        ...state,
        model: action.payload,
      };
    case CHATBOT_ACTIONS.SET_TEMPERATURE:
      return {
        ...state,
        temperature: action.payload,
      };
    case CHATBOT_ACTIONS.SET_MAX_TOKENS:
      return {
        ...state,
        maxTokens: action.payload,
      };
    case CHATBOT_ACTIONS.SET_CHATBOT_MODE:
      // Only clear conversation if mode is actually changing
      if (state.mode !== action.payload) {
        return {
          ...state,
          mode: action.payload,
          // Clear current conversation when mode changes to prevent cross-mode usage
          currentConversationId: null,
          // Clear any pending messages
          messageQueue: [],
          // Reset conversation status
          conversationStatus: CHATBOT_STATUS.IDLE,
          isChatStreaming: false,
        };
      }
      return state;
    case CHATBOT_ACTIONS.SET_ACTIVITY_INSTANCE_ID:
      return {
        ...state,
        activityInstanceId: action.payload,
      };
    case CHATBOT_ACTIONS.SET_ACTIVITY_NAME:
      return {
        ...state,
        activityName: action.payload,
      };
    case CHATBOT_ACTIONS.SET_ACTIVITY_EXTRA_INFO: // ← TODO: Remove after testing
      return {
        ...state,
        activityExtraInfo: action.payload,
      };
    case CHATBOT_ACTIONS.SET_ACTIVITY_ID:
      return {
        ...state,
        activityId: action.payload,
      };
    case CHATBOT_ACTIONS.SET_PRACTICE_EXERCISE:
      return {
        ...state,
        practiceExercise: action.payload,
      };
    case CHATBOT_ACTIONS.SET_PRACTICE_LOADING:
      return {
        ...state,
        practiceIsLoading: action.payload,
      };
    case CHATBOT_ACTIONS.SET_PRACTICE_ERROR:
      return {
        ...state,
        practiceError: action.payload,
      };
    case CHATBOT_ACTIONS.QUEUE_MESSAGE:
      return {
        ...state,
        messageQueue: [...state.messageQueue, action.payload],
      };
    case CHATBOT_ACTIONS.DEQUEUE_MESSAGE:
      return {
        ...state,
        messageQueue: state.messageQueue.slice(1),
      };
    case CHATBOT_ACTIONS.SET_COMPLETED_ACTIVITIES_COUNT:
      return {
        ...state,
        completedActivitiesCount: action.payload,
      };
    case CHATBOT_ACTIONS.ADD_ACTIVITY_RESULT:
      return {
        ...state,
        activityResults: [...state.activityResults, action.payload],
      };
    case CHATBOT_ACTIONS.UPDATE_SESSION_KCS:
      return {
        ...state,
        sessionKCs: action.payload,
      };
    case CHATBOT_ACTIONS.RESET_PRACTICE_SESSION:
      return {
        ...state,
        completedActivitiesCount: 0,
        activityResults: [],
        sessionKCs: [],
        activityExtraInfo: null, // ← TODO: Remove after testing
      };
    case CHATBOT_ACTIONS.SET_CHATBOT_WIDTH:
      return {
        ...state,
        chatbotWidth: action.payload,
      };
    case CHATBOT_ACTIONS.SET_ENABLE_EXTERNAL_SEARCH:
      return {
        ...state,
        enableExternalSearch: action.payload,
      };
    case CHATBOT_ACTIONS.CLEAR_MESSAGE_QUEUE:
      return {
        ...state,
        messageQueue: [],
      };
    case CHATBOT_ACTIONS.SET_IS_STREAMING_CANVAS:
      return {
        ...state,
        streamingCanvas: action.payload || '',
      };
    case CHATBOT_ACTIONS.CLEAR_STREAMING_CANVAS:
      return {
        ...state,
        streamingCanvas: '',
      };
    case CHATBOT_ACTIONS.SET_CANVAS_CONTENT:
      return {
        ...state,
        canvasContent: action.payload || '',
      };
    case CHATBOT_ACTIONS.SET_CONVERSATION_DATA:
      return {
        ...state,
        conversationData: action.payload,
      };
    case CHATBOT_ACTIONS.CLEAR_CONVERSATION_DATA:
      return {
        ...state,
        conversationData: null,
      };
    case CHATBOT_ACTIONS.SET_CANVAS_TOOL_EXECUTING:
      return {
        ...state,
        isCanvasToolExecuting: action.payload,
      };
    default:
      return state;
  }
}

/**
 * Chatbot Context
 */
const ChatbotContext = createContext(null);

/**
 * Chatbot Provider component that manages all chatbot state using useReducer
 */

export const ChatbotProvider = ({ children, initialState = {}, isAIEnabled = true }) => {
  const mergedInitialState = { ...initialChatbotState, ...initialState };
  const [state, dispatch] = useReducer(chatbotReducer, mergedInitialState);

  // IMPORTANT: Hooks MUST be called unconditionally (React rules of hooks)
  // Gate the EFFECTS and LOGIC that use the data, not the hook calls themselves
  const audioManager = useAudioManager();

  // Monitor user changes to reset conversation state on login/SSO/logout
  const { user } = useUser();
  const prevUserGuidRef = useRef();

  // Clear conversation state only when the user actually changes (ignore first render)
  // Gate: Skip when AI features are disabled
  useEffect(() => {
    if (!isAIEnabled) return;

    const previousGuid = prevUserGuidRef.current;
    const currentGuid = user ? user.guid : undefined;
    prevUserGuidRef.current = currentGuid;

    // Ignore first execution after mount to preserve restored conversation state
    if (previousGuid === undefined) return;

    // Reset conversation when user changes
    if (previousGuid !== currentGuid) {
      dispatch({ type: CHATBOT_ACTIONS.SET_CURRENT_CONVERSATION_ID, payload: null });
      dispatch({ type: CHATBOT_ACTIONS.SET_CONVERSATION_STATUS, payload: CHATBOT_STATUS.IDLE });
      dispatch({ type: CHATBOT_ACTIONS.CLEAR_MESSAGE_QUEUE });
    }
  }, [isAIEnabled, user]);

  // Handle external UI state changes from other tabs
  const handleExternalUIStateChange = useCallback(
    (newValue) => {
      if (newValue && state.mode === 'study') {
        // Update conversation ID if it changed externally
        if (newValue.currentConversationId !== state.currentConversationId) {
          dispatch({
            type: CHATBOT_ACTIONS.SET_CURRENT_CONVERSATION_ID,
            payload: newValue.currentConversationId,
          });
        }
        // Update chat open state if it changed externally
        if (newValue.isChatbotOpen !== state.isChatbotOpen) {
          dispatch({
            type: newValue.isChatbotOpen ? CHATBOT_ACTIONS.OPEN_CHATBOT : CHATBOT_ACTIONS.CLOSE_CHATBOT,
          });
        }
        // Update chatbot width if it changed externally
        if (newValue.chatbotWidth !== state.chatbotWidth) {
          dispatch({
            type: CHATBOT_ACTIONS.SET_CHATBOT_WIDTH,
            payload: newValue.chatbotWidth,
          });
        }
      }
    },
    [state.currentConversationId, state.isChatbotOpen, state.chatbotWidth, state.mode]
  );

  // Setup storage sync for UI state
  const { setInternalChange: setUIInternalChange } = useStorageSync('tangerine_chatbot_ui_state', handleExternalUIStateChange);

  /**
   * Save UI state to localStorage with internal change marking
   */
  const saveUIStateToStorage = useCallback(
    (stateToSave) => {
      try {
        // Mark as internal change to prevent feedback loop
        setUIInternalChange(true);
        const uiState = {
          isChatbotOpen: stateToSave.isChatbotOpen,
          chatbotWidth: stateToSave.chatbotWidth,
          // Only persist conversationId in study mode
          currentConversationId: stateToSave.mode === 'study' ? stateToSave.currentConversationId : null,
        };
        localStorage.setItem('tangerine_chatbot_ui_state', JSON.stringify(uiState));
        // Reset flag after a short delay
        setTimeout(() => setUIInternalChange(false), 100);
      } catch (error) {
        console.warn('Failed to save chatbot UI state:', error);
      }
    },
    [setUIInternalChange]
  );

  // Save UI state when it changes
  // Gate: Skip when AI features are disabled
  useEffect(() => {
    if (!isAIEnabled) return;

    saveUIStateToStorage(state);
  }, [isAIEnabled, state.isChatbotOpen, state.currentConversationId, state.chatbotWidth, state.mode, saveUIStateToStorage]);

  // Action creators - using useCallback for frequently used actions only
  const actions = useMemo(
    () => ({
      openChatbot: () => dispatch({ type: CHATBOT_ACTIONS.OPEN_CHATBOT }),
      closeChatbot: () => dispatch({ type: CHATBOT_ACTIONS.CLOSE_CHATBOT }),
      setChatContext: (context) => dispatch({ type: CHATBOT_ACTIONS.SET_CHAT_CONTEXT, payload: context }),
      setCurrentConversationId: (id) => dispatch({ type: CHATBOT_ACTIONS.SET_CURRENT_CONVERSATION_ID, payload: id }),
      setConversations: (conversations) => dispatch({ type: CHATBOT_ACTIONS.SET_CONVERSATIONS, payload: conversations }),
      setConversationStatus: (status) => dispatch({ type: CHATBOT_ACTIONS.SET_CONVERSATION_STATUS, payload: status }),
      setSystemPrompt: (prompt) => dispatch({ type: CHATBOT_ACTIONS.SET_SYSTEM_PROMPT, payload: prompt }),
      setDevMode: (isDevMode) => dispatch({ type: CHATBOT_ACTIONS.SET_DEV_MODE, payload: isDevMode }),
      setModel: (model) => dispatch({ type: CHATBOT_ACTIONS.SET_MODEL, payload: model }),
      setTemperature: (temperature) => dispatch({ type: CHATBOT_ACTIONS.SET_TEMPERATURE, payload: temperature }),
      setMaxTokens: (maxTokens) => dispatch({ type: CHATBOT_ACTIONS.SET_MAX_TOKENS, payload: maxTokens }),
      setChatbotMode: (mode) => dispatch({ type: CHATBOT_ACTIONS.SET_CHATBOT_MODE, payload: mode }),
      setActivityInstanceId: (activityInstanceId) => dispatch({ type: CHATBOT_ACTIONS.SET_ACTIVITY_INSTANCE_ID, payload: activityInstanceId }),
      setActivityName: (activityName) => dispatch({ type: CHATBOT_ACTIONS.SET_ACTIVITY_NAME, payload: activityName }),
      setActivityExtraInfo: (extraInfo) => dispatch({ type: CHATBOT_ACTIONS.SET_ACTIVITY_EXTRA_INFO, payload: extraInfo }), // ← TODO: Remove after testing
      setActivityId: (activityId) => dispatch({ type: CHATBOT_ACTIONS.SET_ACTIVITY_ID, payload: activityId }),
      setPracticeExercise: (exercise) => dispatch({ type: CHATBOT_ACTIONS.SET_PRACTICE_EXERCISE, payload: exercise }),
      setPracticeLoading: (isLoading) => dispatch({ type: CHATBOT_ACTIONS.SET_PRACTICE_LOADING, payload: isLoading }),
      setPracticeError: (error) => dispatch({ type: CHATBOT_ACTIONS.SET_PRACTICE_ERROR, payload: error }),
      queueMessage: (messageData) => dispatch({ type: CHATBOT_ACTIONS.QUEUE_MESSAGE, payload: messageData }),
      dequeueMessage: () => dispatch({ type: CHATBOT_ACTIONS.DEQUEUE_MESSAGE }),
      setCompletedActivitiesCount: (count) => dispatch({ type: CHATBOT_ACTIONS.SET_COMPLETED_ACTIVITIES_COUNT, payload: count }),
      addActivityResult: (result) => dispatch({ type: CHATBOT_ACTIONS.ADD_ACTIVITY_RESULT, payload: result }),
      updateSessionKCs: (kcs) => dispatch({ type: CHATBOT_ACTIONS.UPDATE_SESSION_KCS, payload: kcs }),
      resetPracticeSession: () => dispatch({ type: CHATBOT_ACTIONS.RESET_PRACTICE_SESSION }),
      setChatbotWidth: (width) => dispatch({ type: CHATBOT_ACTIONS.SET_CHATBOT_WIDTH, payload: width }),
      setEnableExternalSearch: (enabled) => dispatch({ type: CHATBOT_ACTIONS.SET_ENABLE_EXTERNAL_SEARCH, payload: enabled }),
      clearMessageQueue: () => dispatch({ type: CHATBOT_ACTIONS.CLEAR_MESSAGE_QUEUE }),
      setIsStreamingCanvas: (text) => dispatch({ type: CHATBOT_ACTIONS.SET_IS_STREAMING_CANVAS, payload: text }),
      clearStreamingCanvas: () => dispatch({ type: CHATBOT_ACTIONS.CLEAR_STREAMING_CANVAS }),
      setCanvasContent: (text) => dispatch({ type: CHATBOT_ACTIONS.SET_CANVAS_CONTENT, payload: text }),
      setConversationData: (data) => dispatch({ type: CHATBOT_ACTIONS.SET_CONVERSATION_DATA, payload: data }),
      clearConversationData: () => dispatch({ type: CHATBOT_ACTIONS.CLEAR_CONVERSATION_DATA }),
      setCanvasToolExecuting: (isExecuting) => dispatch({ type: CHATBOT_ACTIONS.SET_CANVAS_TOOL_EXECUTING, payload: isExecuting }),
    }),
    [dispatch]
  );

  // Create safe actions - no-ops when AI features are disabled
  const safeActions = useMemo(() => {
    if (!isAIEnabled) {
      // Return no-op functions for all actions when AI is disabled
      return Object.fromEntries(Object.keys(actions).map((key) => [key, () => {}]));
    }
    return actions;
  }, [isAIEnabled, actions]);

  // Context value with isAIEnabled flag
  const contextValue = useMemo(
    () => ({
      // State
      ...state,
      // Actions (safe no-ops when disabled)
      ...safeActions,
      // Audio Manager (null when disabled)
      audioManager: isAIEnabled ? audioManager : null,
      // Expose AI enabled status for consumers to check
      isAIEnabled,
    }),
    [state, safeActions, audioManager, isAIEnabled]
  );

  return <ChatbotContext.Provider value={contextValue}>{children}</ChatbotContext.Provider>;
};

ChatbotProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialState: PropTypes.object,
  isAIEnabled: PropTypes.bool,
};

/**
 * Hook to access the chatbot context
 * Safe to use - provider is always mounted, but may be disabled (isAIEnabled=false)
 */
export const useChatbotContext = () => {
  const context = useContext(ChatbotContext);
  if (!context) {
    throw new Error('useChatbotContext must be used within a ChatbotProvider');
  }
  return context;
};

/**
 * Strict hook for AI-only screens
 * Throws if AI features are disabled - use only on routes that require AI
 */
export const useRequiredChatbotContext = () => {
  const context = useChatbotContext();
  if (!context.isAIEnabled) {
    throw new Error('useRequiredChatbotContext: AI features are disabled for this user');
  }
  return context;
};

/**
 * Hook to access chat streaming state
 * Provides encapsulated access to streaming status without exposing internal status strings
 */
export const useChatStreaming = () => {
  const context = useContext(ChatbotContext);
  if (!context) {
    throw new Error('useChatStreaming must be used within a ChatbotProvider');
  }
  return {
    isChatStreaming: context.isChatStreaming,
    conversationStatus: context.conversationStatus, // Still expose for cases needing specific status
  };
};

// Export constants and utilities for use in other components
export { CHATBOT_DEFAULT_WIDTH, CHATBOT_MAX_WIDTH_PERCENTAGE, CHATBOT_MIN_WIDTH, detectChatbotMode };

export default ChatbotContext;
